// ═══════════════════════════════════════════════════════════════════
//  useMinimapCapabilities — Tier-aware Minimap capability resolver.
// ═══════════════════════════════════════════════════════════════════
//
//  Mirrors useAtlasCapabilities. Four levels:
//    - canUseMinimap            base minimap (Advanced+; existing flag)
//    - canUseAtlasMinimap       atlas-doc visual distinction + drill-down (Pro+)
//    - canShowCrossDocRefs      cross-doc ref polylines (Max+)
//    - canShowAdvancedOverlays  heatmap / cursors (Max+)
//
// ═══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { usePlan } from '@/hooks/usePlan';
import { canUse, type PlanTier } from '@/lib/planTiers';

export interface MinimapCapabilities {
  /** Existing minimap navigation (Entity Navigator dropdown). Advanced+. */
  canUseMinimap: boolean;
  /** Atlas-doc visual distinction + drill-down. Pro+. */
  canUseAtlasMinimap: boolean;
  /** Cross-document ref polylines. Max+. */
  canShowCrossDocRefs: boolean;
  /** Heatmap + collaborative-cursor overlays. Max+. */
  canShowAdvancedOverlays: boolean;

  /** Lowest tier that unlocks atlas distinction (for upsell copy). */
  atlasMinimapMinTier: PlanTier;
  /** Lowest tier that unlocks cross-doc refs. */
  crossDocRefsMinTier: PlanTier;
  /** Lowest tier that unlocks overlays. */
  advancedOverlaysMinTier: PlanTier;
  /** Current tier. */
  currentTier: PlanTier;
}

export function useMinimapCapabilities(): MinimapCapabilities {
  const { tier, limits } = usePlan();
  return useMemo(() => {
    const navGate = canUse(limits, 'minimap-nav');
    const atlasGate = canUse(limits, 'minimap-atlas');
    const refsGate = canUse(limits, 'minimap-atlas-refs');
    const overlaysGate = canUse(limits, 'minimap-advanced-overlays');
    return {
      canUseMinimap: navGate.ok,
      canUseAtlasMinimap: atlasGate.ok,
      canShowCrossDocRefs: refsGate.ok,
      canShowAdvancedOverlays: overlaysGate.ok,
      atlasMinimapMinTier: atlasGate.ok ? tier : (atlasGate.requiredTier ?? 'pro'),
      crossDocRefsMinTier: refsGate.ok ? tier : (refsGate.requiredTier ?? 'max'),
      advancedOverlaysMinTier: overlaysGate.ok ? tier : (overlaysGate.requiredTier ?? 'max'),
      currentTier: tier,
    };
  }, [tier, limits]);
}
